import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/lib/auth";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function MyEvolution() {
  const { user } = useAuth();
  const [evals, setEvals] = useState([]);

  useEffect(() => {
    if (!user?.student_id) return;
    api.get(`/evaluations/student/${user.student_id}`).then(r => setEvals(r.data))
      .catch(err => toast.error(formatApiError(err)));
  }, [user]);

  const byCriterion = useMemo(() => {
    const map = {};
    for (const e of evals) {
      Object.entries(e.scores || {}).forEach(([k, v]) => {
        if (!map[k]) map[k] = [];
        map[k].push({ date: e.date, score: v });
      });
    }
    Object.values(map).forEach(arr => arr.sort((a, b) => a.date.localeCompare(b.date)));
    return map;
  }, [evals]);

  return (
    <div data-testid="student-evolution-page">
      <PageHeader eyebrow="Progresso" title="Minha evolução" subtitle="Compare seu desempenho ao longo do tempo." mascot={BRAND.MANGUITA_FORCA} />
      {evals.length < 1 ? (
        <EmptyState title="Sem dados para comparar" description="A evolução aparecerá quando houver avaliações registradas." mascot={BRAND.MANGUITA} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(byCriterion).map(([k, arr]) => {
            const first = arr[0].score;
            const last = arr[arr.length - 1].score;
            const diff = last - first;
            const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
            const color = diff > 0 ? "text-[hsl(var(--vv-lime))]" : diff < 0 ? "text-destructive" : "text-muted-foreground";
            return (
              <div key={k} className="vv-card p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Critério</div>
                    <div className="font-black text-lg">{k}</div>
                  </div>
                  <div className={`flex items-center gap-2 ${color}`}>
                    <Icon className="size-5" />
                    <span className="font-black text-2xl">{diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1)}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Início</div>
                    <div className="font-black text-2xl">{first}</div>
                    <div className="text-xs text-muted-foreground">{arr[0].date}</div>
                  </div>
                  <div className="flex-1 px-3">
                    <div className="h-2 bg-muted rounded-full relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[hsl(var(--vv-blue))] to-[hsl(var(--vv-lime))]" style={{ width: `${(last / 10) * 100}%` }} />
                    </div>
                    <div className="text-center text-xs text-muted-foreground mt-1">{arr.length} avaliaç{arr.length === 1 ? "ão" : "ões"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Atual</div>
                    <div className="font-black text-2xl text-[hsl(var(--vv-lime))]">{last}</div>
                    <div className="text-xs text-muted-foreground">{arr[arr.length - 1].date}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
